import z, { type ZodObject, ZodError } from 'zod';
import { assert } from './lang/assert';
import { rd, RemoteData } from '@passionware/monads';

/**
 * List row: validated entity `data` plus a list {@link id} (stable key for React,
 * selection, drag-select / `data-item-id`). Usually equals `data.id`; use
 * {@link rowLevelValidation.mapToValidatedRows} with `getRowId` when it must differ
 * (e.g. duplicate API ids per row).
 *
 * Rows without a parseable `id` fail whole-array parsing in
 * {@link rowLevelValidation.withRowLevelValidation} — not as a single invalid row —
 * because that indicates a broken payload, not field drift.
 */
export type ValidatedRowValid<T extends { id: string | number }> = {
  isValid: true;
  data: T;
  id: T['id'];
};

export type ValidatedRowInvalid<
  T extends { id: string | number },
  E = ZodError
> = {
  isValid: false;
  error: E;
  /**
   * Wire JSON, unsafely mapped domain snapshot for UI, or `undefined` when
   * {@link unsafeMap} best-effort mapper threw for an invalid row.
   */
  rawItem: unknown | undefined;
  id: T['id'];
};

/** Union of {@link ValidatedRowValid} and {@link ValidatedRowInvalid}. */
export type ValidatedRow<T extends { id: string | number }, E = ZodError> =
  | ValidatedRowValid<T>
  | ValidatedRowInvalid<T, E>;

/** Result of {@link rowLevelValidation.withAllValid} (batch of rows, not a single entity). */
export type AllValidResult<T> =
  | { isValid: true; data: readonly T[] }
  | { isValid: false; error: AllValidError };

export class AllValidError extends Error {
  readonly errors: readonly ZodError[];

  constructor(errors: ZodError[]) {
    super('All rows must be valid');
    this.name = 'AllValidError';
    this.errors = errors;
    Object.setPrototypeOf(this, AllValidError.prototype);
  }

  getErrors(): readonly ZodError[] {
    return this.errors;
  }

  getErrorMessage(): string {
    return this.errors
      .flatMap(error =>
        error.issues.map(issue =>
          issue.path.length > 0
            ? `${issue.path.join('.')}: ${issue.message}`
            : issue.message
        )
      )
      .join('; ');
  }
}

const rowJsonWithIdKey$ = z.object({ id: z.unknown() });

function readRawRowId(item: unknown): unknown {
  const probe = rowJsonWithIdKey$.safeParse(item);
  return probe.success ? probe.data.id : undefined;
}

/** Stable list key from raw JSON `id` when the row failed full item parsing. */
function coerceRowListId(value: unknown): string | number {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  if (typeof value === 'bigint') {
    return Number(value);
  }
  return String(value);
}

/**
 * Re-map a {@link ValidatedRow} to another domain type.
 *
 * - **Valid row:** runs `map(row.data)` with no try/catch (throws propagate). The mapper must
 *   return a value (not `undefined`).
 * - **Invalid row:** runs `map(row.rawItem as TIn)` inside try/catch; on throw, keeps the
 *   original wire `rawItem` so list UIs can still render best-effort cells; on success, same
 *   invalid row shape with domain-shaped `rawItem`.
 */
function unsafeMap<
  TIn extends { id: string | number },
  TOut extends { id: TIn['id'] },
  E = ZodError
>(row: ValidatedRow<TIn, E>, map: (wire: TIn) => TOut): ValidatedRow<TOut, E> {
  if (row.isValid) {
    return { ...row, data: map(row.data) };
  }
  if (row.rawItem === undefined) {
    return row;
  }
  try {
    return {
      ...row,
      rawItem: map(row.rawItem as unknown as TIn)
    };
  } catch {
    return row;
  }
}

/**
 * Applies {@link unsafeMap} to each row in order (same length as input).
 */
function unsafeMapAll<
  TIn extends { id: string | number },
  TOut extends { id: TIn['id'] },
  E = ZodError
>(
  rows: readonly ValidatedRow<TIn, E>[],
  map: (wire: TIn) => TOut
): ValidatedRow<TOut, E>[] {
  return rows.map(row => unsafeMap(row, map));
}

function createRowLevelValidationApi() {
  /** Rows that passed validation, in order. */
  function validDataFromRows<T extends { id: string | number }>(
    rows: ValidatedRow<T>[]
  ): T[] {
    const out: T[] = [];
    for (const row of rows) {
      if (row.isValid) {
        out.push(row.data);
      }
    }
    return out;
  }

  /**
   * Distinct list row {@link ValidatedRow.id} values in row order (for selection cleanup
   * and counts). Uses each row’s `id`, not necessarily `data.id`.
   */
  function referencedIds<T extends { id: string | number }>(
    rows: ValidatedRow<T>[]
  ): T['id'][] {
    const seen = new Set<string>();
    const out: T['id'][] = [];
    for (const row of rows) {
      const k = String(row.id);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(row.id);
    }
    return out;
  }

  /** Wraps already-valid domain data as a successful {@link ValidatedRow}. */
  function validatedRowOf<T extends { id: string | number }>(
    data: T
  ): ValidatedRow<T> {
    return { isValid: true, data, id: data.id };
  }

  /**
   * Maps each item to a successful {@link ValidatedRow}.
   * Optional `getRowId` sets {@link ValidatedRow.id} when it must differ from `item.id`
   * (e.g. composite list key while `data` stays the API model).
   */
  function mapToValidatedRows<T extends { id: string | number }>(
    items: readonly T[],
    getRowId?: (item: T, index: number) => string | number
  ): ValidatedRow<T>[] {
    const pickId = getRowId ?? ((item: T) => item.id);
    return items.map(
      (item, index) =>
        ({
          isValid: true as const,
          data: item,
          id: pickId(item, index)
        }) as ValidatedRow<T>
    );
  }

  /**
   * Builds a Zod schema that parses a full array of unknown rows into
   * {@link ValidatedRow} results (per-item success or {@link ZodError}).
   * Each element must be an object with an `id` key; otherwise the **entire array**
   * fails parsing (payload contract broken). Non-array input fails at the root.
   *
   * Invalid rows get {@link coerceRowListId} on the raw JSON `id` for list keys; the value is
   * typed like a successful parse’s `id` (via {@link z.output} of `itemSchema`).
   *
   * `itemSchema` must be a {@link ZodObject} whose shape includes `id` (same contract as output type).
   */
  function withRowLevelValidation<
    TItemSchema extends ZodObject<{ id: z.ZodType }>
  >(itemSchema: TItemSchema) {
    /** Pins list `id` when Zod would infer `unknown` (still the schema’s field). */
    type Out = z.output<TItemSchema> & { id: string | number };
    return z
      .array(z.unknown())
      .superRefine((items, ctx) => {
        for (let i = 0; i < items.length; i++) {
          const rawId = readRawRowId(items[i]);
          if (rawId === undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                'Row JSON must include an id field (missing id is treated as a broken response, not a per-row validation result)',
              path: [i]
            });
          }
        }
      })
      .transform((items): ValidatedRow<Out>[] =>
        items.map((item): ValidatedRow<Out> => {
          const result = itemSchema.safeParse(item);
          const rawId = readRawRowId(item);
          const id = coerceRowListId(rawId) as Out['id'];
          if (result.success) {
            const data = result.data as Out;
            return {
              isValid: true,
              data,
              id: data.id
            };
          }
          return {
            isValid: false,
            error: result.error,
            rawItem: item,
            id
          };
        })
      );
  }

  function withAllValid<T extends { id: string | number }>(
    rows: ValidatedRow<T>[]
  ): AllValidResult<T> {
    if (rows.some(row => !row.isValid)) {
      return {
        isValid: false,
        error: new AllValidError(
          rows.filter(row => !row.isValid).map(row => row.error)
        )
      };
    }
    return {
      isValid: true,
      data: rows.map(row => {
        assert(row.isValid, 'Row must be valid');
        return row.data;
      })
    };
  }

  function mapWhenAllValid<T extends { id: string | number }, U>(
    data: RemoteData<ValidatedRow<T>[]>,
    mapper: (data: T[]) => U
  ): RemoteData<U> {
    return rd.flatMap(data, rows => {
      const result = withAllValid(rows);
      if (result.isValid) {
        return rd.of(mapper([...result.data]));
      }
      return rd.ofError(result.error);
    });
  }

  return {
    validDataFromRows,
    referencedIds,
    validatedRowOf,
    mapToValidatedRows,
    withRowLevelValidation,
    withAllValid,
    mapWhenAllValid,
    unsafeMap,
    unsafeMapAll
  };
}

/**
 * Row-level validation: per-item Zod results, list helpers, and keys.
 * For {@link RemoteData} of plain rows, use `rd.map(data, rowLevelValidation.mapToValidatedRows)`.
 * For paginated payloads, use e.g. `rd.map(data, ({ items }) => rowLevelValidation.mapToValidatedRows(items))`.
 */
export const rowLevelValidation = createRowLevelValidationApi();
